import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import clsx from 'clsx';
import { TypedMemo } from '../common/types';
import { openSMLUpgradePrompt } from '../../redux/slices/appInfo/slice';
import { SMLUpgradeFeature } from '../music24/config/music24PlanCapabilities';
import { ReadOnlyArtistTag } from './ReadOnlyArtistTag';
import { readOnlyArtistMessages } from './readOnlyArtistMessages';
import { useIsReadOnlyAccount } from './useIsReadOnlyAccount';
/* eslint-disable css-modules/no-unused-class -- the same slim line as the demo notice; this file uses its own slice of it */
import styles from './DemoArtistNotice.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ReadOnlyArtistNoticeProps {
  /** Classes on the wrapper; the shell passes its page padding. */
  className?: string;
}

/**
 * One slim line on top of every page while the active artist is read-only on the plan (a plan went
 * down). "Add an artist" opens the artists offer: one more paid artist on a team plan, else the
 * next plan.
 */
const ReadOnlyArtistNoticeComponent: React.FC<ReadOnlyArtistNoticeProps> = ({ className }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const isReadOnly = useIsReadOnlyAccount();
  const openOffer = useCallback(
    () => dispatch(openSMLUpgradePrompt({ feature: SMLUpgradeFeature.artists })),
    [dispatch]
  );

  if (!isReadOnly) {
    return null;
  }

  return (
    <div className={className}>
      <div
        role="status"
        className={clsx(styles.notice, 'd-flex flex-wrap flex-md-nowrap align-items-center gap-2 mb-3')}
        data-testid="read-only-artist-notice"
      >
        <ReadOnlyArtistTag />
        <span className={styles.text}>{intl.formatMessage(readOnlyArtistMessages.notice)}</span>
        <button type="button" className={clsx(styles.action, 'order-last order-md-0')} onClick={openOffer}>
          {intl.formatMessage(readOnlyArtistMessages.action)}
        </button>
      </div>
    </div>
  );
};

export const ReadOnlyArtistNotice = TypedMemo(ReadOnlyArtistNoticeComponent);
